import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { Color, ColorName } from '../design-system/colors/ColorPalette'
import { Icon, ICONS } from './icon.constants'

/**
 * List of allowed icon sizes in pixels. This is not yet set in stone, but please check in with
 * Design or the Visionaries team if you believe an extra size should be added.
 *
 * As an escape hatch, for one-off icon sizes you can apply a class to the SVG element with the
 * height and width set. Avoid doing this if possible.
 */
export const IconSizePxs = [16, 18, 20, 22, 24] as const
export type IconSizePx = (typeof IconSizePxs)[number]

/**
 * A lightweight SVG icon component that renders icons from the icon library. Supports custom
 * sizing and color theming from the design system palette. Falls back to `currentColor` for color
 * inheritance if no color is specified.
 *
 * @example
 * ``` html
 * <svg
 *   ftrIcon="More"
 *   [iconSize]="24"
 *   iconColor="Grey40"
 * ></svg>
 * ```
 */
@Component({
  selector: 'svg[ftrIcon]',
  template: '<svg:use [attr.href]="iconUrl()" />',
  styleUrl: './icon.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'img',
    '[attr.aria-hidden]': 'true',
    '[attr.data-icon]': 'ftrIcon()',
    '[attr.width]': 'iconSizeRem()',
    '[attr.height]': 'iconSizeRem()',
    '[attr.fill]': 'iconColorStyle()',
  },
})
export class IconComponent {
  /**
   * Name of the icon from the icon library.
   * @see {@link ICONS}
   */
  readonly ftrIcon = input.required<Icon>()

  /**
   * Width and height in pixels. Converts to rem internally.
   * @see {@link IconSizePxs}
   */
  readonly iconSize = input.required<IconSizePx>()

  /**
   * Optional name of fill color from the design system palette. Will use parent 'currentColor' if absent.
   * @see {@link Color}
   */
  readonly iconColor = input<ColorName>()

  protected readonly iconUrl = computed<string>(() => `#${ICONS[this.ftrIcon()]}`)

  protected readonly iconSizeRem = computed<string>(() => `${this.iconSize() / 16}rem`)

  protected readonly iconColorStyle = computed<Color | 'currentColor'>(() => {
    const colorName = this.iconColor()
    return colorName ? Color[colorName] : 'currentColor'
  })
}
